/**
 * tariff_v2 assembly page (spec C4, C5, D10; journey D4 steps 4-6), laid out
 * like the v1 pages users know:
 *   - approval mode: v1 "BOM Review & Approval" (stats banner, construction
 *     report, BOM tree, action bar), plus v2's correction accept/undo,
 *     per-part include / qty / HTS override / unit cost, assembly description;
 *   - results mode: v1 "Tariff Analysis (Assembly Mode)" (info banner,
 *     summary banner with Final HTS, classification card, BOM tree, component
 *     HTS table, component cards).
 * Opens for assembly_analyses records with play_id 'tariff_v2' (AssemblyPageSwitch);
 * the v1 pages are untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaCheck, FaChevronDown, FaChevronRight, FaExclamationTriangle, FaRobot } from 'react-icons/fa';
import { FiAlertCircle, FiCheckCircle, FiClock, FiEdit3, FiFileText, FiGrid, FiInfo, FiLoader, FiPackage, FiSearch } from 'react-icons/fi';
import { IoCubeOutline, IoDocument, IoEye } from 'react-icons/io5';
import FileDetailPanel from '../FileDetail.jsx';
import ThumbnailImage from '../ThumbnailImage';
import AssemblyClassificationV2 from './AssemblyClassificationV2';
import { ExportButtons } from './PartClassificationV2';
import { formatHtsCode, programSummary, rateText } from './shared';
import { buildApprovedTree, decisionsFromApproval, effectiveQuantities, replayCorrections } from './treeModel';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
const POLL_MS = 2000;
const POLL_LIMIT = 450; // 15 minutes
const ACTION_LABEL = {
    match: 'Match to document', exclude: 'Exclude (not a part)', remove_bom_item: 'Remove BOM row',
    reparent: 'Move under another parent', adjust_qty: 'Change quantity', flag: 'Flag for review',
};
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('userToken')}` } });

// ---------------------------------------------------------------------------- pieces

function HtsPicker({ value, onPick }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [busy, setBusy] = useState(false);
    const search = async () => {
        if (query.trim().length < 2) return;
        setBusy(true);
        try {
            const res = await axios.get(`${BASE_URL}/ocr/tariff-v2/hts-search/`, { ...auth(), params: { q: query } });
            setHits(Array.isArray(res.data) ? res.data : []);
        } catch {
            toast.error('HTS search is unavailable');
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="relative inline-block">
            <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(!open); }} title="Search the HTS schedule"
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:text-[#1e5045] bg-white"><FiSearch className="w-3.5 h-3.5" /></button>
            {open && (
                <div className="absolute z-20 right-0 mt-1 w-96 bg-white border border-gray-200 rounded-lg shadow-lg p-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                        <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
                            placeholder="e.g. parts semiconductor machines" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
                        <button type="button" onClick={search} className="px-3 py-1 text-sm rounded bg-[#1e5045] text-white">{busy ? '…' : 'Search'}</button>
                    </div>
                    <ul className="max-h-64 overflow-y-auto mt-2 divide-y">
                        {hits.map((h) => (
                            <li key={h.hts.code}>
                                <button type="button" className={`w-full text-left py-1.5 text-xs hover:bg-gray-50 ${value === h.hts.code ? 'bg-green-50' : ''}`}
                                    onClick={() => { onPick(h.hts.code); setOpen(false); }}>
                                    <span className="font-mono font-medium">{h.hts.code}</span> <span className="text-gray-500">{h.general_rate?.raw}</span>
                                    <div className="text-gray-600">{(h.description_chain || []).slice(-3).join(' › ')}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/** v1 ComponentCard look for one effective part (or one analysed document before approval). */
function ComponentCardV2({ fileId, filename, part, code, description, qty, source, onOpen }) {
    const identity = part?.identity || {};
    const c = part?.classification;
    const { sec301 } = programSummary(c?.primary?.programs);
    return (
        <div id={`file-card-${fileId}`} className="border card p-5 transition-all hover:border-green-800">
            <div className="flex items-start gap-3 p-3">
                <h3 className="text-sm font-semibold text-gray-900 truncate" title={filename}>{filename || 'Unknown File'}</h3>
                {!!sec301.length && <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">301</span>}
                {c?.needs_review && <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">Review</span>}
            </div>
            {(identity.part_number || identity.revision || identity.title) && (
                <div className="px-3 pb-2 space-y-1">
                    <div className="flex items-center gap-3 text-xs">
                        {identity.part_number && <span className="text-gray-700"><span className="text-gray-500">P/N:</span> <span className="font-medium">{identity.part_number}</span></span>}
                        {identity.revision && <span className="text-gray-700"><span className="text-gray-500">Rev:</span> <span className="font-medium">{identity.revision}</span></span>}
                    </div>
                    {identity.title && <p className="text-xs text-gray-600 line-clamp-2">{identity.title}</p>}
                </div>
            )}
            <div className="px-3">
                <div className="h-44 w-full bg-gray-100 rounded-lg overflow-hidden border">
                    {fileId ? <ThumbnailImage fileId={fileId} filename={filename} />
                        : <div className="flex h-full items-center justify-center"><IoDocument className="w-10 h-10 text-gray-400" /></div>}
                </div>
            </div>
            <div className="p-3">
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded font-mono ${code ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                        {code ? formatHtsCode(code) : 'Not classified'}
                    </span>
                    {c && source !== 'override' && <span className="text-xs text-gray-500">({c.primary.confidence}%)</span>}
                    {source === 'override' && <span className="text-xs text-gray-500">(override)</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {c && <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-700 border border-purple-200">
                        Duty: {rateText(c.primary.schedule)}</span>}
                    {qty != null && <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700 border border-gray-200">Qty / assy: {qty}</span>}
                </div>
                {part?.facts?.material?.as_stated && (
                    <div className="mt-2 text-xs text-blue-800 font-medium bg-blue-50 px-2 py-1 rounded inline-block">Material: {part.facts.material.as_stated}</div>
                )}
                {description && <p className="mt-2 text-sm text-gray-600 line-clamp-3">{description}</p>}
            </div>
            <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                <button onClick={onOpen} disabled={!part}
                    className="ml-auto flex items-center gap-1.5 text-sm font-medium text-green-800 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">
                    <IoEye className="w-5 h-5" /> View Analysis
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------- page

export default function BOMApprovalV2Page() {
    const { assemblyId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('approval');
    const [accepted, setAccepted] = useState(new Set());
    const [decisions, setDecisions] = useState({});
    const [overrideInfo, setOverrideInfo] = useState({});
    const [description, setDescription] = useState('');
    const [collapsed, setCollapsed] = useState({});
    const [reportOpen, setReportOpen] = useState(true);
    const [viewing, setViewing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const pollRef = useRef(null);
    const folderName = location.state?.folderName;

    const load = useCallback(async (initialise) => {
        try {
            const res = await axios.get(`${BASE_URL}/ocr/assembly-analysis/${assemblyId}/`, auth());
            const data = res.data;
            setRecord(data);
            if (initialise) {
                const result = data.result || {};
                const corrections = result.analysis?.review?.corrections || [];
                const previous = result.approval;
                const decided = new Map((previous?.approved_tree?.correction_decisions || []).map((d) => [d.correction_id, d.accepted]));
                setAccepted(new Set(corrections.filter((c) => (decided.has(c.correction_id) ? decided.get(c.correction_id) : c.applied))
                    .map((c) => c.correction_id)));
                setDecisions(decisionsFromApproval(previous));
                setDescription(previous?.assembly_description || '');
                setMode(result.status === 'COMPLETED' ? 'results' : 'approval');
            }
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load the assembly analysis');
        } finally {
            setLoading(false);
        }
    }, [assemblyId]);

    useEffect(() => {
        load(true);
        return () => clearTimeout(pollRef.current);
    }, [load]);

    const result = record?.result || {};
    const analysis = result.analysis;
    const corrections = useMemo(() => analysis?.review?.corrections || [], [analysis]);
    const tree = useMemo(() => (analysis ? replayCorrections(analysis.base_tree, corrections, accepted) : null), [analysis, corrections, accepted]);
    const effective = useMemo(() => (tree ? effectiveQuantities(tree, decisions) : {}), [tree, decisions]);
    const partsByFile = useMemo(() => Object.fromEntries((analysis?.parts || []).map((p) => [p.file_id, p])), [analysis]);

    const setDecision = (nodeId, patch) => setDecisions((prev) => ({ ...prev, [nodeId]: { ...(prev[nodeId] || {}), ...patch } }));

    const checkOverride = async (nodeId, code) => {
        if (!code || !code.trim()) {
            setOverrideInfo((prev) => ({ ...prev, [nodeId]: null }));
            return;
        }
        try {
            const res = await axios.get(`${BASE_URL}/ocr/tariff-v2/hts/${encodeURIComponent(code.trim())}/`,
                { ...auth(), validateStatus: (s) => s === 200 || s === 404 });
            setOverrideInfo((prev) => ({
                ...prev,
                [nodeId]: res.status === 200
                    ? { ok: true, text: `${res.data.schedule.description} · ${res.data.schedule.general_rate.raw || 'no rate'}` }
                    : { ok: false, text: `Not in the HTS schedule${res.data.nearest_legal_line ? ` — nearest ${res.data.nearest_legal_line}` : ''}` },
            }));
        } catch {
            setOverrideInfo((prev) => ({ ...prev, [nodeId]: { ok: false, text: 'Could not check the code' } }));
        }
    };

    const poll = (taskId, attempt = 0) => {
        pollRef.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${BASE_URL}/ocr/status/${taskId}/`, auth());
                if (res.data.status === 'SUCCESS') {
                    toast.success('Classification complete!');
                    setSubmitting(false);
                    await load(true);
                    return;
                }
                if (res.data.status === 'FAILED' || res.data.status === 'FAILURE') {
                    toast.error(res.data.error || 'Classification failed');
                    setSubmitting(false);
                    await load(false);
                    return;
                }
            } catch {
                /* transient; keep polling */
            }
            if (attempt < POLL_LIMIT) poll(taskId, attempt + 1);
            else { toast.error('Still running; reopen this page later to see the result.'); setSubmitting(false); }
        }, POLL_MS);
    };

    const submit = async (priorAnswers = []) => {
        if (!tree) return;
        if (Object.entries(overrideInfo).some(([id, info]) => info && !info.ok && decisions[id]?.overrideCode)) {
            toast.error('Fix or clear the HTS overrides that are not in the schedule first');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${BASE_URL}/ocr/tariff-v2/approve-assembly/`, {
                assembly_id: assemblyId,
                approved_tree: buildApprovedTree(tree, decisions, corrections, accepted),
                assembly_description: description,
                prior_answers: priorAnswers,
            }, auth());
            toast.success('BOM approved! Running final classification...');
            poll(res.data.task_id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to approve BOM');
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e5045] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading BOM structure...</p>
                </div>
            </div>
        );
    }
    if (error || !analysis) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <FiAlertCircle className="w-12 h-12 text-[#f26522] mx-auto" />
                    <p className="mt-4 text-[#f26522]">{error || 'This assembly has no tariff analysis'}</p>
                    <button onClick={() => history.goBack()} className="mt-4 px-4 py-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 border border-gray-200">Go Back</button>
                </div>
            </div>
        );
    }

    const report = analysis.report;
    const counts = report.counts;
    const matchRate = counts.bom_rows ? Math.round(((counts.bom_rows - counts.bom_rows_missing) / counts.bom_rows) * 100) : 100;
    const classification = result.classification;
    const folder = folderName || record.metadata?.folder_name;
    const exportUrl = `${BASE_URL}/ocr/tariff-v2/export/assembly/${assemblyId}/`;
    const editable = mode === 'approval';

    const renderNode = (nodeId, level, isRoot = false) => {
        const node = tree.nodes[nodeId];
        const part = node.file_id ? partsByFile[node.file_id] : null;
        const primary = part?.classification?.primary;
        const decision = decisions[nodeId] || {};
        const leftOut = ['excluded', 'invalid_extraction'].includes(node.status);
        const included = decision.included !== false && !leftOut;
        const isMatched = node.status === 'matched';
        const info = overrideInfo[nodeId];
        const open = !collapsed[nodeId];
        return (
            <div key={nodeId} className="select-none">
                <div className={`flex items-start gap-2 py-2 px-3 rounded-lg transition-colors ${isMatched ? 'hover:bg-[#94c43d]/10' : 'hover:bg-[#f26522]/10'} ${included ? '' : 'opacity-50'}`}
                    style={{ marginLeft: `${level * 24}px` }}>
                    {node.children.length ? (
                        <button onClick={() => setCollapsed((p) => ({ ...p, [nodeId]: open }))} className="p-1 mt-0.5 hover:bg-gray-200 rounded transition-colors">
                            {open ? <FaChevronDown className="w-3 h-3 text-gray-500" /> : <FaChevronRight className="w-3 h-3 text-gray-500" />}
                        </button>
                    ) : <span className="w-5" />}
                    {isMatched ? <FiCheckCircle className="w-5 h-5 mt-0.5 text-[#94c43d] flex-shrink-0" /> : <FiAlertCircle className="w-5 h-5 mt-0.5 text-[#f26522] flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            {isRoot && <span className="px-2 py-0.5 text-xs font-bold rounded bg-yellow-400 text-yellow-900">TOP LEVEL</span>}
                            <span className={`font-mono font-medium text-gray-900 truncate ${leftOut ? 'line-through' : ''}`}>{node.part_number || node.part_number_normalized}</span>
                            {!isRoot && (
                                <div className="flex items-center gap-1 text-xs">
                                    <span className="text-gray-500">×</span>
                                    {editable ? (
                                        <input type="number" min="0" step="any" disabled={!included} value={decision.qty ?? node.qty}
                                            onChange={(e) => setDecision(nodeId, { qty: e.target.value })}
                                            className="w-14 px-1.5 py-0.5 text-center border border-gray-300 rounded bg-white text-gray-700 focus:border-[#1e5045] focus:ring-1 focus:ring-[#1e5045] focus:outline-none"
                                            title="Quantity per parent" />
                                    ) : <span className="text-gray-700">{decision.qty ?? node.qty}</span>}
                                    {effective[nodeId] != null && <span className="text-gray-400" title="Quantity per assembly">= {effective[nodeId]} / assy</span>}
                                </div>
                            )}
                            {primary && (
                                <button onClick={() => setViewing(part)} className="px-2 py-0.5 text-xs font-medium rounded font-mono bg-purple-100 text-purple-700 hover:bg-purple-200" title="View part classification">
                                    {formatHtsCode(primary.hts.code)}
                                </button>
                            )}
                            {part?.classification?.needs_review && <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">Review</span>}
                            {node.flags.candidate_non_part && <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">Not a part?</span>}
                            {node.flags.shared_use_count > 1 && <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">Used {node.flags.shared_use_count}×</span>}
                        </div>
                        {node.description && <p className="text-sm text-gray-500 truncate">{node.description}</p>}
                        {node.status === 'missing' && !!node.candidates.length && (
                            <p className="text-xs text-gray-500">Similar uploads: {node.candidates.map((c) => `${c.filename} (${Math.round(c.similarity * 100)}%)`).join(', ')}</p>
                        )}
                        {editable && !isRoot && isMatched && (
                            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                                <label className="flex items-center gap-1 text-gray-600">
                                    <input type="checkbox" checked={included} disabled={leftOut} onChange={(e) => setDecision(nodeId, { included: e.target.checked })} /> Include
                                </label>
                                <input value={decision.overrideCode || ''} placeholder="Override HTS" disabled={!included}
                                    onChange={(e) => setDecision(nodeId, { overrideCode: e.target.value })} onBlur={(e) => checkOverride(nodeId, e.target.value)}
                                    className="w-32 px-1.5 py-0.5 border border-gray-300 rounded font-mono" />
                                <HtsPicker value={decision.overrideCode} onPick={(code) => { setDecision(nodeId, { overrideCode: code }); checkOverride(nodeId, code); }} />
                                {decision.overrideCode && (
                                    <input value={decision.overrideReason || ''} placeholder="Reason for override" onChange={(e) => setDecision(nodeId, { overrideReason: e.target.value })}
                                        className="w-48 px-1.5 py-0.5 border border-gray-300 rounded" />
                                )}
                                <input type="number" min="0" step="any" placeholder="Unit cost (USD)" disabled={!included} value={decision.unitCost ?? ''}
                                    onChange={(e) => setDecision(nodeId, { unitCost: e.target.value })} className="w-32 px-1.5 py-0.5 border border-gray-300 rounded" />
                                {info && <span className={info.ok ? 'text-green-700' : 'text-[#f26522]'}>{info.text}</span>}
                            </div>
                        )}
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${isMatched ? 'bg-[#94c43d]/20 text-[#1e5045]' : 'bg-[#f26522]/20 text-[#f26522]'}`}>
                        {{ matched: 'Matched', missing: 'Missing', excluded: 'Excluded', invalid_extraction: 'Removed' }[node.status]}
                    </span>
                </div>
                {open && node.children.length > 0 && (
                    <div className="border-l-2 border-gray-200 ml-5">{node.children.map((child) => renderNode(child, level + 1))}</div>
                )}
            </div>
        );
    };

    const bomTreeCard = (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 pb-4">
                <h3 className="text-xl font-semibold text-[#1e5045]">BOM Tree Structure</h3>
                <p className="text-sm text-[#225953] mt-1">{editable
                    ? 'Set quantities, include or exclude parts, and override a code or add a unit cost where needed'
                    : 'Assembly hierarchy as approved'}</p>
            </div>
            <div className="px-6 pb-6">
                {renderNode(tree.root_id, 0, true)}
                {!!tree.orphans.length && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-1">Orphan components (outside the top-level folder, in no BOM)</p>
                        {tree.orphans.map((id) => renderNode(id, 0))}
                    </div>
                )}
            </div>
        </div>
    );

    const header = (title, backLabel) => (
        <div className="bg-white border-b border-[#1e5045]/10 sticky top-0 z-10">
            <div className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
                <button onClick={() => history.goBack()} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                    <FaArrowLeft className="w-4 h-4" /><span>{backLabel}</span>
                </button>
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 justify-center">
                        <IoCubeOutline className="w-7 h-7 text-[#1e5045]" />{title}
                    </h1>
                    <p className="text-sm text-gray-500">{folder} • {analysis.parts.length} files analyzed</p>
                </div>
                <ExportButtons url={exportUrl} name="assembly_tariff" />
            </div>
        </div>
    );

    // ------------------------------------------------------------------ results mode
    if (mode === 'results' && classification) {
        const block = classification.classification;
        return (
            <div className="min-h-screen bg-[#f6f4ed]">
                {header('Tariff v2 (Assembly Mode)', 'Back to Project')}
                <div className="p-6 max-w-7xl mx-auto space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <h3 className="font-medium text-blue-900 mb-1">About These Results</h3>
                                <p className="text-sm text-blue-700 mb-3">
                                    These results classify the assembly <strong>exactly as you approved it</strong>: the parts you included,
                                    your quantities and any HTS overrides. Every code is checked against the official HTS schedule
                                    ({analysis.provenance.hts_schedule_revision}). To change the BOM, review and approve it again.
                                </p>
                                <button onClick={() => setMode('approval')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                                    <FiEdit3 className="w-4 h-4" /> Review & Approve BOM
                                </button>
                            </div>
                        </div>
                    </div>

                    {result.classification_error?.error && (
                        <div className="bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg p-4 flex items-start gap-3">
                            <FaExclamationTriangle className="w-5 h-5 text-[#f26522] flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-[#f26522]">The last reclassification failed: {result.classification_error.error}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-start gap-6">
                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#1e5045] to-[#225953] flex items-center justify-center flex-shrink-0">
                                <FiPackage className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold text-gray-900">Assembly Tariff Classification</h2>
                                <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                                    <div className="flex items-center gap-1.5"><FiFileText className="w-4 h-4" /><span>{classification.effective_parts.length} components</span></div>
                                    <div className="flex items-center gap-1.5"><FiClock className="w-4 h-4" /><span>{String(classification.provenance.created_at || '').replace('T', ' ').slice(0, 16)}</span></div>
                                    {block.needs_review
                                        ? <div className="flex items-center gap-1.5 text-amber-700"><FaExclamationTriangle className="w-4 h-4" /><span>Needs review</span></div>
                                        : <div className="flex items-center gap-1.5"><FiCheckCircle className="w-4 h-4 text-[#94c43d]" /><span className="text-[#94c43d]">Completed</span></div>}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-gray-500">Final HTS</div>
                                <div className="text-xl font-mono font-bold text-[#f26522]">{formatHtsCode(block.primary.hts.code)}</div>
                            </div>
                        </div>
                    </div>

                    <AssemblyClassificationV2 result={classification} rerunning={submitting}
                        onRerun={(answers) => submit(answers)} onReclassify={() => setMode('approval')} />

                    {bomTreeCard}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 pb-0"><h3 className="text-lg font-semibold text-gray-900 mb-4">Component HTS Code Details</h3></div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="bg-gray-50 border-y border-gray-200">
                                    <th className="px-6 py-3 text-left font-semibold text-gray-700">HTS Code</th>
                                    <th className="px-6 py-3 text-left font-semibold text-gray-700">Description</th>
                                    <th className="px-6 py-3 text-center font-semibold text-gray-700">Count</th>
                                    <th className="px-6 py-3 text-right font-semibold text-gray-700">Qty / Assembly</th>
                                    <th className="px-6 py-3 text-right font-semibold text-gray-700">Value (USD)</th>
                                </tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                    {classification.rollup.map((r) => (
                                        <tr key={r.hts_code} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-mono text-gray-900 font-medium">{r.hts_code}</td>
                                            <td className="px-6 py-4 text-gray-600">{r.description}</td>
                                            <td className="px-6 py-4 text-center"><span className="bg-[#94c43d]/20 text-[#1e5045] px-2 py-0.5 rounded-full text-xs font-medium">{r.part_count}</span></td>
                                            <td className="px-6 py-4 text-right text-gray-900">{r.total_qty}</td>
                                            <td className="px-6 py-4 text-right text-gray-900">{r.total_value == null ? '-' : r.total_value.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {!!classification.missing_parts.length && (
                            <div className="px-6 py-4 text-sm text-gray-600 border-t border-gray-100">
                                <span className="font-medium text-[#f26522]">Included rows without a code: </span>
                                {classification.missing_parts.map((m) => `${m.part_number || '?'} ×${m.effective_qty} (${m.reason === 'no_document' ? 'no drawing' : 'not classified'})`).join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 pb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FiGrid className="w-5 h-5 text-[#1e5045]" />Component Details</h3>
                            <span className="text-sm text-gray-500">{classification.effective_parts.length} components</span>
                        </div>
                        <div className="px-6 pb-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {classification.effective_parts.map((e) => (
                                <ComponentCardV2 key={e.node_id} fileId={e.file_id} filename={partsByFile[e.file_id]?.filename} part={partsByFile[e.file_id]}
                                    code={e.effective_hts.code} description={e.effective_description} qty={e.effective_qty} source={e.source}
                                    onOpen={() => setViewing(partsByFile[e.file_id])} />
                            ))}
                        </div>
                    </div>
                </div>
                {viewing && <PartPanel part={viewing} onClose={() => setViewing(null)} />}
            </div>
        );
    }

    // ------------------------------------------------------------------ approval mode
    const qualityChip = (label, value) => (
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${value === 'good' ? 'bg-green-100 text-green-700'
            : value === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{label}: {value.replace('_', ' ')}</span>
    );

    return (
        <div className="min-h-screen bg-[#f6f4ed]">
            {header('BOM Review & Approval', 'Back')}
            <div className="p-6 max-w-7xl mx-auto space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1e5045] to-[#225953] flex items-center justify-center">
                                <FiPackage className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Assembly Bill of Materials</h2>
                                <p className="text-sm text-gray-500">Review the reconstructed BOM structure before final classification</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-center"><div className="text-2xl font-bold text-[#94c43d]">{counts.matched}</div><div className="text-xs text-gray-500">Matched</div></div>
                            <div className="text-center"><div className="text-2xl font-bold text-[#f26522]">{counts.missing}</div><div className="text-xs text-gray-500">Missing</div></div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${matchRate >= 80 ? 'text-[#94c43d]' : matchRate >= 50 ? 'text-[#f26522]' : 'text-red-500'}`}>{matchRate}%</div>
                                <div className="text-xs text-gray-500">Match Rate</div>
                            </div>
                        </div>
                    </div>
                </div>

                {counts.missing > 0 && (
                    <div className="bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg p-4 flex items-start gap-3">
                        <FaExclamationTriangle className="w-5 h-5 text-[#f26522] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-[#f26522]">{counts.missing} BOM item{counts.missing > 1 ? 's' : ''} could not be matched to uploaded documents</p>
                            <p className="text-sm text-gray-700 mt-1">Missing items may affect classification accuracy. You can still proceed with approval, or go back to upload the missing files.</p>
                        </div>
                    </div>
                )}
                {result.classification_error?.error && (
                    <div className="bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg p-4 flex items-start gap-3">
                        <FaExclamationTriangle className="w-5 h-5 text-[#f26522] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-[#f26522]">The last classification attempt failed: {result.classification_error.error}</p>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setReportOpen(!reportOpen)}>
                        <div className="flex items-center gap-3 flex-wrap">
                            <FiFileText className="w-5 h-5 text-[#1e5045]" />
                            <h3 className="text-lg font-semibold text-gray-900">BOM Construction Report</h3>
                            {qualityChip('Folders', analysis.review.assessment.folder_structure)}
                            {qualityChip('BOM tables', analysis.review.assessment.bom_tables)}
                            {!!corrections.length && (
                                <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
                                    <FaRobot className="w-3 h-3" /> {corrections.length} AI correction{corrections.length > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${reportOpen ? 'rotate-180' : ''}`} />
                    </div>
                    {reportOpen && (
                        <div className="p-4 space-y-4">
                            <div className="bg-[#f6f4ed] rounded-lg p-4">
                                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2"><FiInfo className="w-4 h-4" /> Construction summary</h4>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                    <div><span className="text-gray-500">Matched:</span><span className="ml-2 font-semibold text-[#94c43d]">{counts.matched}</span></div>
                                    <div><span className="text-gray-500">Missing:</span><span className="ml-2 font-semibold text-[#f26522]">{counts.missing}</span></div>
                                    <div><span className="text-gray-500">Excluded:</span><span className="ml-2 font-semibold">{counts.excluded}</span></div>
                                    <div><span className="text-gray-500">No evidence:</span><span className="ml-2 font-semibold">{counts.unstructured}</span></div>
                                    <div><span className="text-gray-500">Orphans:</span><span className="ml-2 font-semibold">{counts.orphans}</span></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-700">{analysis.review.narrative}</p>
                            {!!report.warnings.length && (
                                <ul className="text-sm text-amber-800 space-y-1">
                                    {report.warnings.map((w, i) => <li key={i} className="flex gap-2"><FaExclamationTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />{w}</li>)}
                                </ul>
                            )}
                            {!!corrections.length && (
                                <div>
                                    <h4 className="font-medium text-gray-700 mb-2">AI corrections — untick to undo, tick to accept</h4>
                                    <ul className="space-y-2">
                                        {corrections.map((c) => {
                                            const target = analysis.base_tree.nodes[c.target_node_id];
                                            return (
                                                <li key={c.correction_id} className="text-sm border border-gray-200 rounded-lg p-3">
                                                    <label className="flex items-start gap-3 cursor-pointer">
                                                        <input type="checkbox" className="mt-1" checked={accepted.has(c.correction_id)}
                                                            onChange={(e) => setAccepted((prev) => {
                                                                const next = new Set(prev);
                                                                if (e.target.checked) next.add(c.correction_id); else next.delete(c.correction_id);
                                                                return next;
                                                            })} />
                                                        <span>
                                                            <span className="font-medium">{ACTION_LABEL[c.action]}</span>: <span className="font-mono">{target?.part_number || target?.part_number_normalized}</span>
                                                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${c.confidence === 'high' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                                {c.confidence} confidence{c.applied ? ' · applied automatically' : ' · suggestion'}
                                                            </span>
                                                            <div className="text-xs text-gray-600 mt-0.5">{c.reasoning}</div>
                                                        </span>
                                                    </label>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {bomTreeCard}

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <label className="block text-lg font-semibold text-gray-900 mb-1">Assembly Description</label>
                    <p className="text-sm text-gray-500 mb-2">What is this assembly and what is it used for? The classification is more reliable with it.</p>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                        placeholder="e.g. Wafer carrier assembly holding 6-inch wafers inside the K465i MOCVD reactor during deposition."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1e5045]" />
                </div>

                <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-[#1e5045]/10 p-4">
                    <button onClick={() => classification && setMode('results')} disabled={!classification}
                        className="px-4 py-2 text-gray-600 hover:text-[#1e5045] hover:bg-[#f6f4ed] rounded-lg transition-colors disabled:opacity-40">
                        Skip to Results
                    </button>
                    <div className="flex items-center gap-3">
                        <button onClick={() => history.goBack()} className="px-4 py-2 bg-[#f6f4ed] text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-[#1e5045]/10">Cancel</button>
                        <button onClick={() => submit(result.approval?.prior_answers || [])} disabled={submitting}
                            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${submitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#1e5045] text-white hover:bg-[#163d35]'}`}>
                            {submitting ? <><FiLoader className="w-4 h-4 animate-spin" /><span>Approving...</span></>
                                : <><FaCheck className="w-4 h-4" /><span>Approve BOM</span></>}
                        </button>
                    </div>
                </div>
            </div>
            {viewing && <PartPanel part={viewing} onClose={() => setViewing(null)} />}
        </div>
    );
}

/** The standard file detail panel, opened on the part's Tariff Analysis tab. */
function PartPanel({ part, onClose }) {
    return (
        <FileDetailPanel file={{ filename: part.filename, _id: part.file_id }} fileId={part.file_id}
            filename={part.filename} onClose={onClose} defaultTab="tariff_v2" />
    );
}
